/**
 * What is behind a bar (T7.30, design.md § 7.7).
 *
 * Clicking a bar opens the room's deliverables in PLAN ORDER with their states,
 * filtered to that party by the `Owner` from § 7.6. The bar merges several
 * schedule rows into one span and hides any gap between them; this list is what
 * carries the detail, which is the reason the merge is acceptable at all.
 *
 * Plan order is the order the rows arrive in — the workbook's own order, which
 * `buildModel` stamps `__i` against. The rows are read straight off
 * `model.raw.deliverables` rather than walked out of the room tree, because the
 * tree groups by pillar and sliver and would impose its own order. The pillar
 * and sliver headings here are context for a row, not a re-ordering of it: the
 * first time a pillar or sliver appears in plan order is where its heading goes.
 *
 * Nothing is truncated. A deliverable's full name is the stated product
 * requirement, so the name wraps rather than ellipsing, and the `title` carries
 * it as well for anything that does clip.
 *
 * Every rendered word that is one of the nine comes from `useTerms()`. The state
 * a chip IS stays the canonical key — `s-published` is a class name, "Published"
 * is only what happens to be printed in it today.
 */
import { useEffect, useRef } from 'react';
import {
  STATE_CLASS,
  isDeliverableState,
  ownerOf,
  type BlueprintModel,
  type DeliverableRow,
} from '@blueprint/shared';
import { useTerms } from '../../lib/terms';
import { stateTermKey } from '../blueprint/stateTerms';
import { PARTY_LABEL, fmtDay, type Party, type TimelineBar, type TimelineRow } from './layout';

/** A deliverable with its pillar and sliver, in plan order. */
interface Line {
  row: DeliverableRow;
  pillar: string;
  sliver: string;
}

function linesFor(model: BlueprintModel, roomID: string, party: Party): Line[] {
  return model.raw.deliverables
    .filter((d) => String(d.RoomID) === roomID && ownerOf(d) === party)
    .map((d) => ({
      row: d,
      pillar: String(d.PillarName ?? '').trim(),
      sliver: String(d.SliverName ?? '').trim(),
    }));
}

export function RoomDeliverables({
  model,
  row,
  bar,
  onClose,
}: {
  model: BlueprintModel;
  row: TimelineRow;
  bar: TimelineBar;
  onClose: () => void;
}) {
  const terms = useTerms();
  const panel = useRef<HTMLDivElement>(null);

  // Escape closes, and focus moves into the panel so a keyboard can reach the
  // close button without tabbing back through the whole timeline.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    panel.current?.focus();
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const lines = linesFor(model, row.roomID, bar.party);
  const deliverable = terms.noun('deliverable');
  const deliverables = terms.plural('deliverable');
  const heading = `${row.name} — ${PARTY_LABEL[bar.party]}`;

  let pillar = '\u0000';
  let sliver = '\u0000';

  return (
    <div className="tl-modal" onPointerDown={onClose}>
      <div
        className="tl-popup"
        role="dialog"
        aria-modal="true"
        aria-label={`${heading}: ${deliverables}`}
        tabIndex={-1}
        ref={panel}
        onPointerDown={(e) => e.stopPropagation()}
      >
        <div className="tl-popup-head">
          <div>
            <h4>{heading}</h4>
            <p>
              {fmtDay(bar.start)} – {fmtDay(bar.end)} · {bar.days} working days ·{' '}
              {lines.length} {lines.length === 1 ? deliverable.toLowerCase() : deliverables.toLowerCase()}
            </p>
          </div>
          <button type="button" className="btn" onClick={onClose}>
            Close
          </button>
        </div>

        {lines.length ? (
          <ol className="tl-popup-list">
            {lines.map((l, k) => {
              const newPillar = l.pillar !== pillar;
              const newSliver = newPillar || l.sliver !== sliver;
              pillar = l.pillar;
              sliver = l.sliver;
              const raw = String(l.row.State ?? '');
              // An unrecognised state reads as Draft, exactly as the chip does.
              const state = isDeliverableState(raw) ? raw : 'Draft';
              return (
                <li key={l.row.id ?? `${l.row.__i ?? k}`}>
                  {newPillar ? <b className="pil">{l.pillar || terms.noun('pillar')}</b> : null}
                  {newSliver && l.sliver ? <span className="slv">{l.sliver}</span> : null}
                  <span className="ln">
                    <span className="nm" title={String(l.row.DeliverableName ?? '')}>
                      {String(l.row.DeliverableName ?? '')}
                    </span>
                    <span className={`chip ${STATE_CLASS[state]}`}>
                      <span className="dot" />
                      {terms.state(stateTermKey(state))}
                    </span>
                  </span>
                </li>
              );
            })}
          </ol>
        ) : (
          <p className="tl-popup-empty">
            No {deliverables.toLowerCase()} in this {terms.noun('room').toLowerCase()} belong to the{' '}
            {PARTY_LABEL[bar.party].toLowerCase()}.
          </p>
        )}
      </div>
    </div>
  );
}
